import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Title, Text, Card, Group, ThemeIcon, Box, Stack,
  Button, Divider, UnstyledButton, Drawer, TextInput,
  Textarea, ActionIcon, Skeleton, Tooltip,
  NumberInput,
  Checkbox,
  Badge,
  ScrollArea,
  ColorPicker
} from '@mantine/core';
import { IconBook2, IconUser, IconArrowLeft, IconPlus, IconPencil, IconTrash, IconRefresh, IconCheck, IconX, IconMapPin } from '@tabler/icons-react';
import { Table, ConfirmDialog, } from '@/components';
import { toast } from '@/components/ui/providers/AlertProvider';
import {  useCareZoneService, CareZone } from '@actschurch/ccms-sdk';

const ALL_CATEGORIES = [
  { label: 'Acts Care Courses',    icon: IconBook2, link: '/LookUp/ActsCareCourses',    active: false },
  { label: 'Acts Care Counseling', icon: IconUser,  link: '/LookUp/ActsCareCounseling', active: false  },
   { label: 'Locations', icon: IconMapPin,  link: '/LookUp/Location', active: true  },
];

const EMPTY: CareZone = { zoneID: 0, name: '', description: '', color: '', isActive: false,  createdDateTime: 0, modifiedDateTime: 0, };

function ShimmerRow() {
  return (
    <Table.Tr>
      {Array.from({ length: 5 }).map((_, i) => (
        <Table.Td key={i}><Skeleton height={14} radius="sm" /></Table.Td>
      ))}
    </Table.Tr>
  );
}

export default function ZonePage() {
  const navigate = useNavigate();
  const careZone  = useCareZoneService();

  const [zones, setZones]             = useState<any[]>([]);
  const [loading, setLoading]         = useState(false);
  const [addOpen, setAddOpen]         = useState(false);
  const [editOpen, setEditOpen]       = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [form, setForm]               = useState<CareZone>(EMPTY);
  const [selectedID, setSelectedID]   = useState(0);


  const fetchAll = useCallback(async () => {
    setLoading(true);
    try { setZones(await careZone.getAll()); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [careZone]);

  useEffect(() => { fetchAll(); }, []);

  const resetForm = () => setForm(EMPTY);

  const handleAdd = async () => {
    setLoading(true);
    try { await careZone.create(form);
         await fetchAll();
          resetForm(); 
         setAddOpen(false);
          toast.success('Created', 'Zone created successfully.');
        }
    catch (e) { console.error(e);
          toast.error('Error', 'Failed to create Zone.');
        setLoading(false); }
  };

  const handleUpdate = async () => {
    setLoading(true);
    try { await careZone.update(selectedID, form);
         await fetchAll();
          resetForm();
          setEditOpen(false);
        toast.success('Updated', 'Zone updated successfully.');
         }
    catch (e) { console.error(e);
          toast.error('Error', 'Failed to update Zone.');
         setLoading(false);
         }
  };

  const handleDelete = async () => {
    setLoading(true);
    try { await careZone.delete(selectedID);
         await fetchAll();
          setConfirmOpen(false); 
           toast.warning('Deleted', 'Zone deleted successfully.');
        }
    catch (e) { console.error(e);
               toast.error('Error', 'Failed to delete Zone.');
               setLoading(false);  
             }
  };

  const openEdit = (n: CareZone) => {
    setSelectedID(n.zoneID); setForm({ ...n }); setEditOpen(true);
  };

  // Needs has no statusID/icon/isActive — simpler form
  const ZoneForm = (
    <Stack>
            <TextInput label="Name" value={form.name ?? ''}
              onChange={(e) => setForm({ ...form, name: e.currentTarget.value })} />
              <Text>Choose Colour</Text>
              <ColorPicker
                value={form.color || '#4CAF50'}
                onChange={(value) => setForm({ ...form, color: value })}
                format="hex"
                swatches={[
                  '#e03131', '#c2255c', '#9c36b5', '#3b5bdb', '#1971c2',
                  '#0c8599', '#099268', '#2f9e44', '#e8590c', '#f08c00',
                ]}
                swatchesPerRow={10}
                fullWidth
              />
            <Textarea label="Description" value={form.description ?? ''}
              onChange={(e) => setForm({ ...form, description: e.currentTarget.value })} />
            <Checkbox label="Is Active" checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.currentTarget.checked })} />
          </Stack>
  );

  return (
    <Container size="xl" py="xl">
     <Drawer opened={addOpen} onClose={() => { setAddOpen(false); resetForm(); }}
        title="Add zone" position="right" size="md" padding="md">
        {ZoneForm}
        <Group justify="flex-end" mt="lg">
          <Button variant="subtle" onClick={() => { setAddOpen(false); resetForm(); }}>Cancel</Button>
          <Button onClick={handleAdd}>Save</Button>
        </Group>
      </Drawer>

      <Drawer opened={editOpen} onClose={() => { setEditOpen(false); resetForm(); }}
        title="Update zone" position="right" size="md" padding="md">
        {ZoneForm}
        <Group justify="flex-end" mt="lg">
          <Button variant="subtle" onClick={() => { setEditOpen(false); resetForm(); }}>Cancel</Button>
          <Button onClick={handleUpdate}>Update</Button>
        </Group>
      </Drawer>

      <ConfirmDialog opened={confirmOpen} onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete} type="danger" title="Delete this need?"
        message="This action cannot be undone." confirmLabel="Delete" cancelLabel="Cancel" />

      <Group align="flex-start" gap="xl" wrap="nowrap">
        <Box w={220} style={{ flexShrink: 0 }}>
          <Button variant="subtle" leftSection={<IconArrowLeft size={14} />}
            mb="lg" px={0} onClick={() => navigate('/LookUp/ActsCareCounseling')}>
            All Lookups
          </Button>
          <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb="sm">Categories</Text>
          <Stack gap={4}>
            {ALL_CATEGORIES.map((cat) => (
              <UnstyledButton key={cat.label} onClick={() => navigate(cat.link)}
                style={(theme) => ({
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 10px', borderRadius: theme.radius.sm,
                  backgroundColor: cat.active ? theme.colors.yellow[9] + '33' : 'transparent',
                  color: cat.active ? theme.colors.yellow[4] : theme.colors.gray[4],
                  fontWeight: cat.active ? 600 : 400,
                })}>
                <ThemeIcon variant={cat.active ? 'light' : 'subtle'}
                  color={cat.active ? 'yellow' : 'gray'} size="sm" radius="sm">
                  <cat.icon size={14} />
                </ThemeIcon>
                <Text size="sm">{cat.label}</Text>
              </UnstyledButton>
            ))}
          </Stack>
        </Box>

        <Divider orientation="vertical" />

        <Box style={{ flex: 1 }}>
          <Group justify="space-between" mb="lg">
            <Group gap="md">
              <ThemeIcon variant="light" color="yellow" size="lg" radius="md">
                <IconUser size={18} />
              </ThemeIcon>
              <div>
                <Title order={3}>Zone</Title>
                <Text size="sm" c="dimmed">Manage zones within areas for location grouping and reporting</Text>
              </div>
            </Group>
            <Group>
              <Tooltip label="Refresh">
                <ActionIcon variant="subtle" onClick={fetchAll} loading={loading}>
                  <IconRefresh size={16} />
                </ActionIcon>
              </Tooltip>
              <Button leftSection={<IconPlus size={16} />} onClick={() => setAddOpen(true)}>Add Item</Button>
            </Group>
          </Group>

          <Card withBorder radius="md" padding="lg">
            <ScrollArea offsetScrollbars>
            <Table tableVariant="default">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>ID</Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Color</Table.Th>
                  <Table.Th>Description</Table.Th>
                  <Table.Th>Status</Table.Th>
                  
                  <Table.Th></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {loading
                  ? Array.from({ length: 5 }).map((_, i) => <ShimmerRow key={i} />)
                  : zones.map((n) => (
                    <Table.Tr key={n.zoneID}>
                      <Table.Td>{n.zoneID}</Table.Td>
                      <Table.Td fw={600}>{n.name}</Table.Td>
                      <Table.Td>
                        {n.color ? (
                          <Group gap={6}>
                            <Box w={16} h={16} style={{ borderRadius: 4, backgroundColor: n.color, border: '1px solid rgba(255,255,255,0.15)', flexShrink: 0 }} />
                            <Text size="sm">{n.color}</Text>
                          </Group>
                        ) : '—'}
                      </Table.Td>
                      <Table.Td>{n.description}</Table.Td>
                       <Table.Td>
                        <Badge color={n.isActive ? 'green' : 'red'} variant="light" size="sm">
                        {n.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                    </Table.Td>
                      <Table.Td>
                        <Group gap={4}>
                          <ActionIcon variant="subtle" onClick={() => openEdit(n)}>
                            <IconPencil size={16} color="orange" />
                          </ActionIcon>
                          <ActionIcon variant="subtle" onClick={() => { setSelectedID(n.zoneID); setConfirmOpen(true); }}>
                            <IconTrash size={16} color="red" />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
              </Table.Tbody>
            </Table>
            </ScrollArea>
          </Card>
        </Box>
      </Group>
    </Container>
  );
}